import React, { useState, useEffect } from 'react';
import { customerProfileAPI } from '../../services/api';
import './CustomerProfiles.css';

const CustomerProfiles = () => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [formData, setFormData] = useState({
    profile_name: '',
    customer_name: '',
    billing_address: '',
    shipping_address: '',
    customer_gstin: '',
    state_name: '',
    state_code: '',
    customer_contact: '',
    is_default: false,
  });

  useEffect(() => {
    fetchProfiles();
  }, []);

  const fetchProfiles = async () => {
    try {
      const response = await customerProfileAPI.getAll();
      if (response.success) {
        setProfiles(response.data);
      }
    } catch (err) {
      alert('Failed to fetch customer profiles');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingProfile) {
        const response = await customerProfileAPI.update(editingProfile.id, formData);
        if (response.success) {
          alert('Customer profile updated successfully');
          fetchProfiles();
          resetForm();
        }
      } else {
        const response = await customerProfileAPI.create(formData);
        if (response.success) {
          alert('Customer profile created successfully');
          fetchProfiles();
          resetForm();
        }
      }
    } catch (err) {
      alert('Failed to save customer profile');
    }
  };

  const handleEdit = (profile) => {
    setEditingProfile(profile);
    setFormData({
      profile_name: profile.profile_name,
      customer_name: profile.customer_name,
      billing_address: profile.billing_address,
      shipping_address: profile.shipping_address || '',
      customer_gstin: profile.customer_gstin || '',
      state_name: profile.state_name || '',
      state_code: profile.state_code || '',
      customer_contact: profile.customer_contact || '',
      is_default: profile.is_default,
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this customer profile?')) {
      try {
        const response = await customerProfileAPI.delete(id);
        if (response.success) {
          alert('Customer profile deleted successfully');
          fetchProfiles();
        }
      } catch (err) {
        alert('Failed to delete customer profile');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      profile_name: '',
      customer_name: '',
      billing_address: '',
      shipping_address: '',
      customer_gstin: '',
      state_name: '',
      state_code: '',
      customer_contact: '',
      is_default: false,
    });
    setEditingProfile(null);
    setShowForm(false);
  };

  if (loading) {
    return <div className="loading">Loading customer profiles...</div>;
  }

  return (
    <div className="customer-profiles-container">
      <div className="profiles-header">
        <h2>Customer Profiles</h2>
        <button
          onClick={() => setShowForm(!showForm)}
          className="create-btn"
        >
          {showForm ? 'Cancel' : '+ Add Profile'}
        </button>
      </div>

      {showForm && (
        <div className="profile-form-card">
          <h3>{editingProfile ? 'Edit' : 'Create'} Customer Profile</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-field">
                <label>Profile Name *</label>
                <input
                  type="text"
                  name="profile_name"
                  value={formData.profile_name}
                  onChange={handleInputChange}
                  placeholder="e.g., ABC Company, XYZ Corp"
                  required
                />
              </div>
              <div className="form-field">
                <label>
                  <input
                    type="checkbox"
                    name="is_default"
                    checked={formData.is_default}
                    onChange={handleInputChange}
                  />
                  {' '}Set as Default
                </label>
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label>Customer Name *</label>
                <input
                  type="text"
                  name="customer_name"
                  value={formData.customer_name}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-field">
                <label>Customer GSTIN</label>
                <input
                  type="text"
                  name="customer_gstin"
                  value={formData.customer_gstin}
                  onChange={handleInputChange}
                  maxLength="15"
                  placeholder="15-digit GSTIN"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-field full-width">
                <label>Billing Address *</label>
                <textarea
                  name="billing_address"
                  value={formData.billing_address}
                  onChange={handleInputChange}
                  required
                  rows="3"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-field full-width">
                <label>Shipping Address</label>
                <textarea
                  name="shipping_address"
                  value={formData.shipping_address}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Leave blank if same as billing address"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label>State Name *</label>
                <input
                  type="text"
                  name="state_name"
                  value={formData.state_name}
                  onChange={handleInputChange}
                  placeholder="e.g., Maharashtra"
                  required
                />
              </div>
              <div className="form-field">
                <label>State Code *</label>
                <input
                  type="text"
                  name="state_code"
                  value={formData.state_code}
                  onChange={handleInputChange}
                  placeholder="e.g., 27"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label>Contact (Phone/Email)</label>
                <input
                  type="text"
                  name="customer_contact"
                  value={formData.customer_contact}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="form-actions">
              <button type="button" onClick={resetForm} className="cancel-btn">
                Cancel
              </button>
              <button type="submit" className="submit-btn">
                {editingProfile ? 'Update' : 'Create'} Profile
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="profiles-grid">
        {profiles.length === 0 ? (
          <div className="empty-state">
            <p>No customer profiles found. Create your first profile!</p>
          </div>
        ) : (
          profiles.map((profile) => (
            <div key={profile.id} className="profile-card">
              {profile.is_default && <span className="default-badge">Default</span>}
              <h3>{profile.profile_name}</h3>
              <div className="profile-details">
                <p><strong>{profile.customer_name}</strong></p>
                <p className="address">{profile.billing_address}</p>
                {profile.customer_gstin && <p>GSTIN: {profile.customer_gstin}</p>}
                {profile.state_name && <p>State: {profile.state_name} ({profile.state_code})</p>}
                {profile.customer_contact && <p>Contact: {profile.customer_contact}</p>}
              </div>
              <div className="profile-actions">
                <button onClick={() => handleEdit(profile)} className="edit-btn">
                  Edit
                </button>
                <button onClick={() => handleDelete(profile.id)} className="delete-btn">
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default CustomerProfiles;
